import { createSelector } from 'reselect'

export const getSelection = createSelector(
  [
    state => state.selection,
  ],
  selection => selection,
)

export const getSelectionByKey = createSelector(
  [
    object => object.state.selection,
    object => object.key,
  ],
  (selection, key) => selection[key],
)

export const getNodeSelectionById = createSelector(
  [
    object => object.state,
    object => object.id,
  ],
  (state, id) => {
    const {
      selection,
      core: { nodes },
    } = state
    const activeNode = nodes.find(node => node.id === id)
    const activeSelection = (activeNode && activeNode.children.filter(sel => selection.nodes.includes(sel)))

    return ({
      minSelect: (activeNode && activeNode.minSelect),
      maxSelect: (activeNode && activeNode.maxSelect),
      selection: activeSelection,
    })
  },
)

export const getNodeProgressById = createSelector(
  [
    object => object.state,
    object => object.id,
  ],
  (state, id) => {
    const activeNode = state.core.nodes.find(node => node.id === id)
    if ((activeNode !== undefined) && (activeNode.minSelect > 1)) {
      const selection = state.selection.nodes
      if (Array.isArray(selection)) {
        const selected = activeNode.children.filter(sel => selection.includes(sel))

        return {
          selected: selected.length,
          total: Math.min(activeNode.minSelect, activeNode.children.length),
        }
      }
    }
    return false
  },
)

export const getNodeSiblingsById = createSelector(
  [
    object => object.state,
    object => object.id,
  ],
  (state, id) => {
    const activeNode = state.core.nodes.find(node => node.id === id)
    let parentNode
    let parentSelected = []
    let sibling
    if ((activeNode !== undefined) && ('parent' in activeNode)) {
      parentNode = state.core.nodes.find(node => node.id === activeNode.parent)
      const selection = state.selection.nodes
      if (Array.isArray(selection) && selection.includes(activeNode.id)) {
        if ((parentNode !== undefined) && ('children' in parentNode)) {
          parentSelected = parentNode.children.filter(sel => selection.includes(sel))
          sibling = parentNode.children.find(child => !parentSelected.includes(child))
        }
      }
    }
    return ({
      parent: (parentNode && parentNode.id),
      next: (parentNode && (parentSelected.length < (parentNode.maxSelect)) && (sibling !== undefined) && sibling),
    })
  },
)

export const getFirstIncomplete = createSelector(
  [
    object => object.state,
    object => object.topSteps,
  ],
  (state, topSteps) => {
    const steps = topSteps.map(step => (
      state[step]
    ))

    return steps.reduce((index, step, currentIndex) => {
      const key = step.config.selectionKey
      if (key !== undefined) {
        if (state.selection[key] === null) {
          return Math.min(index, currentIndex)
        }
        if (Array.isArray(state.selection[key])) {
          if (step.rootNode && !state.selection[key].includes(step.rootNode.id)) {
            return Math.min(index, currentIndex)
          }
        }
        if ((Object.keys(state.selection[key]).length === 0) && (state.selection[key].constructor === Object)) {
          return Math.min(index, currentIndex)
        }
      }
      return index
    }, steps.length)
  },
)

export const getIsIncompleteByKey = createSelector(
  [
    object => object.state,
    object => object.key,
  ],
  (state, key) => {
    const step = state[key]
    const { selectionKey } = step.config

    if (selectionKey !== undefined) {
      if (state.selection[selectionKey] === null) {
        return true
      }
      if (Array.isArray(state.selection[selectionKey])) {
        if (step.rootNode && !state.selection[selectionKey].includes(step.rootNode.id)) {
          return true
        }
      }
      if ((Object.keys(state.selection[selectionKey]).length === 0) && (state.selection[selectionKey].constructor === Object)) {
        return true
      }
      return false
    }
    return true
  },
)


// Get involved nodes
const removeSelectionOptions = (nodeSelection, nodes, nodeId, involvedNodes) => {
  const node = nodes.find(el => el.id === nodeId)

  if ('children' in node) {
    const childSelection = node.children.filter(sel => nodeSelection.includes(sel))

    if (childSelection.length > 0) {
      involvedNodes.concat(childSelection.forEach(id => removeSelectionOptions(nodeSelection, nodes, id, involvedNodes)))
    }
  }
  involvedNodes.push(nodeId)
  return involvedNodes
}

const updateParentAdd = (nodeSelection, nodes, node, involvedNodes) => {
  if (node !== undefined) {
    const parent = nodes.find(el => el.id === node.parent)
    if (parent !== undefined) {
      const parentSelected = nodeSelection.includes(node.parent)
      const parentSelection = nodeSelection.filter(sel => ((sel === node.id) || parent.children.includes(sel)))
      if (!parentSelected) {
        if ((parentSelection.length + 1) >= Math.min(parent.minSelect, parent.children.length)) {
          involvedNodes.push(parent.id)
          return updateParentAdd(nodeSelection, nodes, parent, involvedNodes)
        }
        return involvedNodes
      } else if (parentSelection.length >= parent.maxSelect) {
        return removeSelectionOptions(nodeSelection, nodes, parentSelection[0], involvedNodes)
      }
    }
  }
  return involvedNodes
}

const updateParentRemove = (nodeSelection, nodes, node, involvedNodes) => {
  if (node !== undefined) {
    const parent = nodes.find(el => el.id === node.parent)

    if (parent !== undefined) {
      const parentSelected = nodeSelection.includes(node.parent)

      if (parentSelected) {
        const parentSelection = nodeSelection.filter(sel => ((sel !== node.id) && parent.children.includes(sel)))
        if (parentSelection.length < Math.min(parent.minSelect, parent.children.length)) {
          involvedNodes.push(parent.id)
          return updateParentRemove(nodeSelection, nodes, parent, involvedNodes)
        }
      }
    }
  }
  return involvedNodes
}

export const getInvolvedNodes = createSelector(
  [
    object => object.state,
    object => object.node,
  ],
  (state, node) => {
    const { nodes } = state.core
    const nodeSelection = state.selection.nodes
    const addOption = !nodeSelection.includes(node.id)

    return (addOption ? (
      updateParentAdd(nodeSelection, nodes, node, [node.id])
    ) : (
      updateParentRemove(nodeSelection, nodes, node, [node.id])
    ))
  },
)




// WEBPACK FOOTER //
// ./src/modules/selection/selectors.js